import { RefObject, useEffect } from 'react';

/**
 * Verifies that a <video> with media-scoped <source> children actually
 * selected the file matching the viewport, and corrects it if not.
 *
 * Chrome and Firefox honor `media` on <video><source> and pick the right
 * file with zero JS. Safari's source selection has been observed choosing
 * the mobile file on desktop viewports, so after hydration we compare the
 * browser's pick against matchMedia and override `video.src` when they
 * disagree (setting `src` takes precedence over <source> children).
 *
 * Source selection can settle before or after this effect runs, so the
 * check is re-attempted on media events and short timeouts; the final
 * attempt forces the desired file even if the browser reported no
 * selection at all.
 */
export function useResponsiveVideoSource(
  videoRef: RefObject<HTMLVideoElement | null>,
  {
    desktopSrc,
    mobileSrc,
    minWidth = 1024,
  }: { desktopSrc: string; mobileSrc: string; minWidth?: number }
): void {
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const desired = window.matchMedia(`(min-width: ${minWidth}px)`).matches
      ? desktopSrc
      : mobileSrc;

    let done = false;
    const apply = () => {
      done = true;
      cleanup();
      video.src = desired;
      video.load();
      video.play().catch(() => undefined);
    };
    const verify = (force: boolean) => {
      if (done) return;
      if (video.currentSrc) {
        if (video.currentSrc.endsWith(desired)) {
          done = true;
          cleanup();
        } else {
          apply();
        }
      } else if (force) {
        apply();
      }
    };

    const check = () => verify(false);
    const events = ['loadstart', 'loadedmetadata', 'error', 'suspend'];
    events.forEach((e) => video.addEventListener(e, check));
    const t1 = window.setTimeout(check, 300);
    const t2 = window.setTimeout(() => verify(true), 1500);
    function cleanup() {
      events.forEach((e) => video?.removeEventListener(e, check));
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    }
    verify(false);

    return cleanup;
  }, [videoRef, desktopSrc, mobileSrc, minWidth]);
}
